import {useSortable} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import {Choice, useTranslationContext} from 'platform/components';

import {suffixTestId, TestIdProps} from 'shared/web';

function GripIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <g fill="currentColor" stroke="none">
        <circle cx="9" cy="6" r="1.5" />
        <circle cx="9" cy="12" r="1.5" />
        <circle cx="9" cy="18" r="1.5" />
        <circle cx="15" cy="6" r="1.5" />
        <circle cx="15" cy="12" r="1.5" />
        <circle cx="15" cy="18" r="1.5" />
      </g>
    </svg>
  );
}

type SortableRowProps = {
  id: string;
  columnKey: string;
  order: 'ASC' | 'DESC';
  availableColumns: {key: string; headerName: string}[];
  onChangeColumn: (newKey: string) => void;
  onChangeOrder: (order: 'ASC' | 'DESC') => void;
  onRemove: () => void;
} & TestIdProps;

export function SortableRow(props: SortableRowProps) {
  const t = useTranslationContext();
  const {attributes, listeners, setNodeRef, transform, transition, isDragging} = useSortable({
    id: props.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.45 : 1,
  };

  const columnOptions = props.availableColumns.map((c) => ({value: c.key, label: c.headerName}));
  const directionOptions = [
    {value: 'ASC' as const, label: t('page.datagrid.labels.sortAscending')},
    {value: 'DESC' as const, label: t('page.datagrid.labels.sortDescending')},
  ];

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2 py-1.5"
      data-testid={suffixTestId(`sortRow-${props.columnKey}`, props)}
    >
      <span className="text-[#B3BAC5] cursor-grab flex-shrink-0" {...attributes} {...listeners}>
        <GripIcon />
      </span>
      <div className="flex-1 min-w-0">
        <Choice
          label=""
          name={`sortColumn-${props.columnKey}`}
          options={columnOptions}
          value={props.columnKey}
          onChange={(value) => {
            if (value) {
              props.onChangeColumn(value);
            }
          }}
          isNotClearable
          menuInPortal
          data-testid={suffixTestId(`sortRow-${props.columnKey}-column`, props)}
        />
      </div>
      <div className="w-[160px] flex-shrink-0">
        <Choice
          label=""
          name={`sortOrder-${props.columnKey}`}
          options={directionOptions}
          value={props.order}
          onChange={(value) => {
            if (value) {
              props.onChangeOrder(value);
            }
          }}
          isNotClearable
          menuInPortal
          data-testid={suffixTestId(`sortRow-${props.columnKey}-order`, props)}
        />
      </div>
      <button
        type="button"
        className="border-0 bg-transparent cursor-pointer flex items-center justify-center rounded p-1 flex-shrink-0 text-[#BF2600]"
        onClick={props.onRemove}
        aria-label={t('page.datagrid.button.removeSort')}
        data-testid={suffixTestId(`sortRow-${props.columnKey}-remove`, props)}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
        </svg>
      </button>
    </div>
  );
}
